import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAssetTransactions, fetchUserAssetBalances, exchangeAsset, transferAssetP2P, requestAssetWithdrawal } from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import {
  AssetHistoryItem,
  AssetBalance,
  WithdrawFormState,
  ExchangeModalState,
  DepositModalState
} from './AssetHistory.types';

interface P2PModalState {
  showP2PModal: boolean;
  p2pTargetAsset: any;
  p2pToUserId: string;
  p2pAmount: string;
  p2pReason: string;
  p2pProcessing: boolean;
}

export interface AssetHistoryLogicReturn {
  // Translated props
  title: string;
  emptyText: string;
  
  // Redux state
  assetTransactionsState: {
    items: AssetHistoryItem[];
    loading: boolean;
    error: any;
    pagination: any;
  };
  balances: AssetBalance[];
  totalBalance: number;
  totalReceived: number;
  balancesLoading: boolean;
  balancesError: any;
  isUserLoggedIn: boolean;
  userInfo: any;
  companyId: number | null;
  
  // Local state
  page: number;
  assetData: AssetHistoryItem[];
  totalSum: number;
  totalGotMileage: number;
  totalCount: number;
  apiError: string | null;
  isLoading: boolean;
  assetTypes: any[];
  selectedAssetType: string;
  assetTypeBalances: {[key: string]: number};
  
  // Modal states
  withdrawState: WithdrawFormState;
  exchangeState: ExchangeModalState;
  depositState: DepositModalState;
  p2pState: P2PModalState;
  
  // Preview states
  previewSelectedAssetType: string;
  previewShowWithdrawForm: boolean;
  previewShowExchangeModal: boolean;
  
  // Functions
  handleAssetTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  toggleWithdrawForm: () => void;
  openDepositModal: (assetType: any) => void;
  closeDepositModal: () => void;
  copyWalletAddress: (address: string) => void;
  closeExchangeModal: () => void;
  openExchangeModal: (fromAsset: AssetBalance) => void;
  handleExchangeAmountChange: (amount: string) => void;
  handleExchangeSubmit: () => Promise<void>;
  handleWithdrawAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleWithdrawSubmit: () => Promise<void>;
  openP2PModal: (asset: any) => void;
  closeP2PModal: () => void;
  handleP2PSubmit: () => Promise<void>;
  setPage: (page: number) => void;
  
  // State setters
  setWithdrawState: React.Dispatch<React.SetStateAction<WithdrawFormState>>;
  setExchangeState: React.Dispatch<React.SetStateAction<ExchangeModalState>>;
  setP2PState: React.Dispatch<React.SetStateAction<P2PModalState>>;
  setPreviewSelectedAssetType: React.Dispatch<React.SetStateAction<string>>;
  setPreviewShowWithdrawForm: React.Dispatch<React.SetStateAction<boolean>>;
  setPreviewShowExchangeModal: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Utils
  t: (key: string) => string;
  dispatch: any;
  isPreview: boolean;
  style: React.CSSProperties;
  className: string;
}

// Helper functions
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const formatAmount = (amount: number): string => {
  if (isNaN(amount)) return '0';
  return amount.toLocaleString();
};

const isP2PEnabled = (asset: any) => {
  if (!asset.p2p || !asset.p2p.enabled) return false;
  const companyIds = Object.keys(asset.companyDisplayNames || {});
  const companyId = companyIds.length > 0 ? Number(companyIds[0]) : null;
  if (!companyId || !asset.p2p.companySettings) return false;
  const companySetting = asset.p2p.companySettings.find((cs: any) => Number(cs.companyId) === companyId);
  return companySetting && companySetting.p2pEnabled === true;
};

export const useAssetHistoryLogic = (props: any): AssetHistoryLogicReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  
  // Extract props
  const {
    style = {},
    className = "mileage-history-wrapper padding-tb-40",
    isPreview = false,
    componentProps = {}
  } = props;

  // Extract component props with translations
  const title = componentProps?.title ? t(componentProps.title) : t("자산 내역");
  const emptyText = componentProps?.emptyText ? t(componentProps.emptyText) : t("자산 내역이 없습니다.");

  // Redux state
  const assetTransactions = useSelector((state: any) => state.withcookie?.assetTransactions);
  const assetBalances = useSelector((state: any) => state.withcookie?.userAssetBalances);
  const userInfo = useSelector((state: any) => state.withcookie?.userInfo || null);
  const withcookieData = useSelector((state: any) => state.withcookie?.withcookieData);
  const isUserLoggedIn = useSelector((state: any) => state.withcookie?.isUserLoggedIn || false);

  // Get company ID
  const companyId = withcookieData?.skin?.company?.id || withcookieData?.company?.id;

  // Process asset transactions state
  const assetTransactionsState = useMemo(() => {
    return {
      items: assetTransactions?.items || [],
      loading: assetTransactions?.loading || false,
      error: assetTransactions?.error || null,
      pagination: assetTransactions?.pagination || { page: 1, totalPages: 1, totalItems: 0 }
    };
  }, [assetTransactions]);

  // Process balances
  const {
    balances,
    totalBalance,
    totalReceived,
    loading: balancesLoading,
    error: balancesError
  } = useMemo(() => {
    if (Array.isArray(assetBalances)) {
      return {
        balances: assetBalances,
        totalBalance: 0,
        totalReceived: 0,
        loading: false,
        error: null
      };
    }

    return {
      balances: assetBalances?.balances || assetBalances?.assetBalances || [],
      totalBalance: assetBalances?.totalBalance || 0,
      totalReceived: assetBalances?.totalReceived || 0,
      loading: assetBalances?.loading || false,
      error: assetBalances?.error || null
    };
  }, [assetBalances]);

  // Local state
  const [page, setPage] = useState(1);
  const [assetData, setAssetData] = useState<AssetHistoryItem[]>([]);
  const [totalSum, setTotalSum] = useState(0);
  const [totalGotMileage, setTotalGotMileage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [assetTypes, setAssetTypes] = useState<any[]>([]);
  const [selectedAssetType, setSelectedAssetType] = useState('');
  const [assetTypeBalances, setAssetTypeBalances] = useState<{[key: string]: number}>({});

  // Modal states
  const [withdrawState, setWithdrawState] = useState<WithdrawFormState>({
    showWithdrawForm: false,
    withdrawAmount: '',
    withdrawToAddress: '',
    withdrawMemo: '',
    withdrawProcessing: false,
    selectedCompanyId: '',
    withdrawDisabledMessage: '',
    feeAmount: 0,
    receiveAmount: 0
  });

  const [exchangeState, setExchangeState] = useState<ExchangeModalState>({
    showExchangeModal: false,
    exchangeFromAsset: null,
    exchangeToAsset: null,
    exchangeAmount: '',
    exchangeReason: '',
    exchangeProcessing: false,
    exchangeResult: null,
    exchangeFeeAmount: 0,
    exchangeReceiveAmount: 0,
    exchangeDisabledMessage: ''
  });

  const [depositState, setDepositState] = useState<DepositModalState>({
    showDepositModal: false,
    depositAsset: null
  });

  const [p2pState, setP2PState] = useState<P2PModalState>({
    showP2PModal: false,
    p2pTargetAsset: null,
    p2pToUserId: '',
    p2pAmount: '',
    p2pReason: '',
    p2pProcessing: false
  });

  // Preview states
  const [previewSelectedAssetType, setPreviewSelectedAssetType] = useState('all');
  const [previewShowWithdrawForm, setPreviewShowWithdrawForm] = useState(false);
  const [previewShowExchangeModal, setPreviewShowExchangeModal] = useState(false);

  // Handlers
  const handleAssetTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAssetType(e.target.value);
  }, []);

  const toggleWithdrawForm = useCallback(() => {
    setWithdrawState(prev => ({
      ...prev,
      showWithdrawForm: !prev.showWithdrawForm,
      withdrawAmount: prev.showWithdrawForm ? prev.withdrawAmount : '',
      withdrawToAddress: prev.showWithdrawForm ? prev.withdrawToAddress : '',
      withdrawMemo: prev.showWithdrawForm ? prev.withdrawMemo : '',
      feeAmount: prev.showWithdrawForm ? prev.feeAmount : 0,
      receiveAmount: prev.showWithdrawForm ? prev.receiveAmount : 0
    }));
  }, []);

  const openDepositModal = useCallback((assetType: any) => {
    setDepositState(prev => ({
      ...prev,
      showDepositModal: true,
      depositAsset: assetType
    }));
  }, []);

  const closeDepositModal = useCallback(() => {
    setDepositState(prev => ({
      ...prev,
      showDepositModal: false,
      depositAsset: null
    }));
  }, []);

  const copyWalletAddress = useCallback((address: string) => {
    navigator.clipboard.writeText(address)
      .then(() => {
        alert(t("주소가 클립보드에 복사되었습니다."));
      })
      .catch(err => {
        console.error('주소 복사 실패:', err);
        alert(t("주소 복사에 실패했습니다."));
      });
  }, [t]);

  const closeExchangeModal = useCallback(() => {
    setExchangeState(prev => ({
      ...prev,
      showExchangeModal: false,
      exchangeFromAsset: null,
      exchangeToAsset: null,
      exchangeAmount: '',
      exchangeReason: '',
      exchangeResult: null,
      exchangeFeeAmount: 0,
      exchangeReceiveAmount: 0,
      exchangeDisabledMessage: ''
    }));
  }, []);

  const openExchangeModal = useCallback((fromAsset: AssetBalance) => {
    setExchangeState(prev => ({
      ...prev,
      showExchangeModal: true,
      exchangeFromAsset: fromAsset,
      exchangeAmount: '',
      exchangeReason: '',
      exchangeFeeAmount: 0,
      exchangeReceiveAmount: 0,
      exchangeDisabledMessage: ''
    }));
  }, []);

  const handleExchangeAmountChange = useCallback((amount: string) => {
    if (!exchangeState.exchangeFromAsset || !exchangeState.exchangeToAsset) return;
    
    const numAmount = parseFloat(amount) || 0;
    const fromAsset = exchangeState.exchangeFromAsset;
    const toAsset = exchangeState.exchangeToAsset;
    
    // Calculate fee and receive amount based on asset settings
    let feeAmount = 0;
    let receiveAmount = 0;
    
    if (fromAsset.exchange?.companySettings && companyId) {
      const companySetting = fromAsset.exchange.companySettings.find((cs: any) => 
        Number(cs.companyId) === Number(companyId)
      );
      
      if (companySetting?.exchangeSettings) {
        const { feeType, fee, feePercent, value } = companySetting.exchangeSettings;
        
        if (feeType === 'FIXED') {
          feeAmount = fee || 0;
        } else if (feeType === 'PERCENT') {
          feeAmount = numAmount * (feePercent / 100);
        }
        
        const afterFee = numAmount - feeAmount;
        receiveAmount = afterFee * (value || 1);
      }
    }
    
    setExchangeState(prev => ({
      ...prev,
      exchangeAmount: amount,
      exchangeFeeAmount: feeAmount,
      exchangeReceiveAmount: receiveAmount
    }));
  }, [exchangeState.exchangeFromAsset, exchangeState.exchangeToAsset, companyId]);

  const handleExchangeSubmit = useCallback(async () => {
    if (!exchangeState.exchangeFromAsset || !exchangeState.exchangeToAsset) return;
    
    const numAmount = parseFloat(exchangeState.exchangeAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      alert(t("올바른 환전 금액을 입력해주세요."));
      return;
    }
    
    setExchangeState(prev => ({ ...prev, exchangeProcessing: true }));
    
    try {
      const result = await dispatch(exchangeAsset({
        fromAssetTypeId: exchangeState.exchangeFromAsset.assetTypeId,
        toAssetTypeId: exchangeState.exchangeToAsset.assetTypeId,
        amount: numAmount,
        reason: exchangeState.exchangeReason || t("환전")
      })).unwrap();
      
      if (result) {
        alert(t("환전이 완료되었습니다."));
        closeExchangeModal();
        dispatch(fetchUserAssetBalances());
        dispatch(fetchAssetTransactions(1));
      }
    } catch (error) {
      console.error('환전 실패:', error);
      alert(t("환전에 실패했습니다. 다시 시도해주세요."));
    } finally {
      setExchangeState(prev => ({ ...prev, exchangeProcessing: false }));
    }
  }, [exchangeState, dispatch, t, closeExchangeModal]);

  const handleWithdrawAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = e.target.value;
    const numAmount = parseFloat(amount) || 0;
    
    // Calculate fee based on selected asset
    const selectedAsset = balances.find((b: any) => b.assetTypeId === parseInt(selectedAssetType));
    if (!selectedAsset || !companyId) {
      setWithdrawState(prev => ({ ...prev, withdrawAmount: amount }));
      return;
    }
    
    let feeAmount = 0;
    const companySetting = selectedAsset.withdrawal?.companySettings?.find(
      (cs: any) => Number(cs.companyId) === Number(companyId)
    );
    
    if (companySetting?.withdrawalSettings) {
      const { feeType, fee, feePercent } = companySetting.withdrawalSettings;
      
      if (feeType === 'FIXED') {
        feeAmount = fee || 0;
      } else if (feeType === 'PERCENT') {
        feeAmount = numAmount * (feePercent / 100);
      }
    }
    
    const receiveAmount = Math.max(0, numAmount - feeAmount);
    
    setWithdrawState(prev => ({
      ...prev,
      withdrawAmount: amount,
      feeAmount,
      receiveAmount
    }));
  }, [selectedAssetType, balances, companyId]);

  const handleWithdrawSubmit = useCallback(async () => {
    const numAmount = parseFloat(withdrawState.withdrawAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      alert(t("올바른 출금 금액을 입력해주세요."));
      return;
    }
    
    if (!withdrawState.withdrawToAddress) {
      alert(t("출금 주소를 입력해주세요."));
      return;
    }
    
    setWithdrawState(prev => ({ ...prev, withdrawProcessing: true }));
    
    try {
      const result = await dispatch(requestAssetWithdrawal({
        assetTypeId: parseInt(selectedAssetType),
        companyId: companyId || 0,
        amount: numAmount,
        toAddress: withdrawState.withdrawToAddress,
        desc: withdrawState.withdrawMemo
      })).unwrap();
      
      if (result) {
        alert(t("출금 요청이 성공적으로 접수되었습니다."));
        toggleWithdrawForm();
        dispatch(fetchUserAssetBalances());
        dispatch(fetchAssetTransactions(1));
      }
    } catch (error) {
      console.error('출금 요청 실패:', error);
      alert(t("출금 요청에 실패했습니다. 다시 시도해주세요."));
    } finally {
      setWithdrawState(prev => ({ ...prev, withdrawProcessing: false }));
    }
  }, [withdrawState, selectedAssetType, dispatch, t, toggleWithdrawForm]);

  const openP2PModal = useCallback((asset: any) => {
    setP2PState(prev => ({
      ...prev,
      showP2PModal: true,
      p2pTargetAsset: asset,
      p2pToUserId: '',
      p2pAmount: '',
      p2pReason: ''
    }));
  }, []);

  const closeP2PModal = useCallback(() => {
    setP2PState(prev => ({
      ...prev,
      showP2PModal: false,
      p2pTargetAsset: null,
      p2pToUserId: '',
      p2pAmount: '',
      p2pReason: ''
    }));
  }, []);

  const handleP2PSubmit = useCallback(async () => {
    const numAmount = parseFloat(p2pState.p2pAmount);
    
    if (!p2pState.p2pToUserId) {
      alert(t("받는 사람의 유저 ID를 입력해주세요."));
      return;
    }
    
    if (isNaN(numAmount) || numAmount <= 0) {
      alert(t("올바른 전송 금액을 입력해주세요."));
      return;
    }
    
    setP2PState(prev => ({ ...prev, p2pProcessing: true }));
    
    try {
      const result = await dispatch(transferAssetP2P({
        assetTypeId: p2pState.p2pTargetAsset.assetTypeId,
        toInsungId: p2pState.p2pToUserId,
        amount: numAmount,
        reason: p2pState.p2pReason || t("P2P 전송")
      })).unwrap();
      
      if (result) {
        alert(t("P2P 전송이 완료되었습니다."));
        closeP2PModal();
        dispatch(fetchUserAssetBalances());
        dispatch(fetchAssetTransactions(1));
      }
    } catch (error) {
      console.error('P2P 전송 실패:', error);
      alert(t("P2P 전송에 실패했습니다. 다시 시도해주세요."));
    } finally {
      setP2PState(prev => ({ ...prev, p2pProcessing: false }));
    }
  }, [p2pState, dispatch, t, closeP2PModal]);

  // Effects for loading data
  useEffect(() => {
    if (isUserLoggedIn && !isPreview) {
      dispatch(fetchUserAssetBalances());
      dispatch(fetchAssetTransactions(1));
    }
  }, [isUserLoggedIn, isPreview, dispatch]);

  // Update asset types when balances change
  useEffect(() => {
    if (balances && balances.length > 0) {
      const types = balances.map((balance: any) => ({
        id: balance.assetTypeId,
        name: balance.displayName || balance.assetName || '',
        code: balance.assetCode || '',
        iconUrl: balance.iconUrl || ''
      }));
      setAssetTypes(types);
      
      // Calculate balances by type
      const balancesByType: {[key: string]: number} = {};
      balances.forEach((balance: any) => {
        balancesByType[balance.assetTypeId] = 
          typeof balance.amount === 'string' ? parseFloat(balance.amount) : balance.amount;
      });
      setAssetTypeBalances(balancesByType);
    }
  }, [balances]);

  // Update asset data when transactions change
  useEffect(() => {
    setAssetData(assetTransactionsState.items);
    setTotalCount(assetTransactionsState.pagination.totalItems || 0);
    
    // Calculate totals
    const received = assetTransactionsState.items.reduce((sum: any, item: any) => {
      if (item.amount > 0) {
        return sum + item.amount;
      }
      return sum;
    }, 0);
    
    setTotalGotMileage(received);
    setTotalSum(assetTransactionsState.items.reduce((sum: any, item: any) => sum + Math.abs(item.amount), 0));
  }, [assetTransactionsState]);

  return {
    // Translated props
    title,
    emptyText,
    
    // Redux state
    assetTransactionsState,
    balances,
    totalBalance,
    totalReceived,
    balancesLoading,
    balancesError,
    isUserLoggedIn,
    userInfo,
    companyId,
    
    // Local state
    page,
    assetData,
    totalSum,
    totalGotMileage,
    totalCount,
    apiError,
    isLoading,
    assetTypes,
    selectedAssetType,
    assetTypeBalances,
    
    // Modal states
    withdrawState,
    exchangeState,
    depositState,
    p2pState,
    
    // Preview states
    previewSelectedAssetType,
    previewShowWithdrawForm,
    previewShowExchangeModal,
    
    // Functions
    handleAssetTypeChange,
    toggleWithdrawForm,
    openDepositModal,
    closeDepositModal,
    copyWalletAddress,
    closeExchangeModal,
    openExchangeModal,
    handleExchangeAmountChange,
    handleExchangeSubmit,
    handleWithdrawAmountChange,
    handleWithdrawSubmit,
    openP2PModal,
    closeP2PModal,
    handleP2PSubmit,
    setPage,
    
    // State setters
    setWithdrawState,
    setExchangeState,
    setP2PState,
    setPreviewSelectedAssetType,
    setPreviewShowWithdrawForm,
    setPreviewShowExchangeModal,
    
    // Utils
    t,
    dispatch,
    isPreview,
    style,
    className
  };
};

// Export helper functions
export { formatDate, formatAmount, isP2PEnabled };